<template>
  <b-overlay :show="loading">
    <section>
      <div>
        <div>
          <h2>Personal Information</h2>
        </div>

        <b-alert v-if="!emailVerified" variant="danger" :show="true" class="mb-1">
          <div class="alert-body font-small-2">
            <b-card-text class="d-flex justify-content-between align-items-center">
              Email verification pending
              <b-button v-if="!emailVerificationSent" variant="outline-primary" size="sm" @click="resendVerificationEmail">Resend Verification Email</b-button>
            </b-card-text>
          </div>
        </b-alert>

        <b-alert v-if="!phoneVerified" variant="danger" :show="true" class="mb-1">
          <div class="alert-body font-small-2">
            <b-card-text class="d-flex justify-content-between align-items-center">
              Phone verification pending
              <b-button v-if="!phoneVerificationSent" variant="outline-primary" size="sm" @click="requestPhoneVerification">Verify Phone</b-button>
            </b-card-text>
          </div>
        </b-alert>

        <div>
          <b-card>
            <div class="card-body">
              <div class="mb-2">
                <b-media class="text-center">
                  <b-avatar :src="avatarPath" :text="avatarText(`${userData.member_profile.first_name} ${userData.member_profile.last_name}`)" :variant="`light-${resolveStatusVariant(getValueFromSource(userData, 'status'))}`" size="10rem" />
                </b-media>
              </div>

              <!--first name -->
              <div class="mb-1">
                <b-form-group label="First Name" label-for="firstname">
                  <div class="custom-input">
                    <span class="leading">
                      <Person />
                    </span>
                    <input id="firstname" :value="userData.member_profile.first_name" readonly disabled>
                  </div>
                </b-form-group>
              </div>

              <!--last name -->
              <div class="mb-1">
                <b-form-group label="Last Name" label-for="lastname">
                  <div class="custom-input">
                    <span class="leading">
                      <Person />
                    </span>
                    <input id="lastname" :value="userData.member_profile.last_name" readonly disabled>
                  </div>
                </b-form-group>
              </div>

              <!--Email -->
              <div class="mb-1">
                <b-form-group label="Email" label-for="email">
                  <div class="custom-input">
                    <span class="leading">
                      <RegistrationMail />
                    </span>
                    <input id="email" :value="userData.email" readonly disabled>
                    <span v-if="emailVerified" class="trailing">
                      <small class="text-success">verified</small>
                      <feather-icon class="text-success" icon="CheckCircleIcon" />
                    </span>
                    <span v-else class="trailing">
                      <small class="text-danger">pending </small>
                      <feather-icon class="text-danger" icon="AlertCircleIcon" />
                    </span>
                  </div>
                </b-form-group>
              </div>

              <!--Phone -->
              <div class="mb-1">
                <b-form-group label="Phone" label-for="phone">
                  <div class="custom-input">
                    <span class="leading">
                      <RegistrationPhone />
                    </span>
                    <input id="phone" :value="userData.phone" readonly disabled>
                    <span v-if="phoneVerified" class="trailing">
                      <small class="text-success">verified</small>
                      <feather-icon class="text-success" icon="CheckCircleIcon" />
                    </span>
                    <span v-else class="trailing">
                      <small class="text-danger">pending </small>
                      <feather-icon class="text-danger" icon="AlertCircleIcon" />
                    </span>
                  </div>
                </b-form-group>
              </div>
            </div>
          </b-card>
        </div>
      </div>
    </section>
  </b-overlay>
</template>

<script>
import {
  BCard,
  BMedia,
  BAvatar,
  BButton,
  BOverlay,
  BFormGroup,
  BAlert,
  BCardText,
} from "bootstrap-vue";
import { cloneDeep, get } from "lodash";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import Person from '@/assets/svg/Person.svg';
import RegistrationMail from '@/assets/svg/RegistrationMail.svg';
import RegistrationPhone from '@/assets/svg/RegistrationPhone.svg';

export default {
  name: "Update",
  components: {
    BCard,
    BMedia,
    BAlert,
    Person,
    BAvatar,
    BButton,
    BOverlay,
    BCardText,
    BFormGroup,
    RegistrationMail,
    RegistrationPhone,
  },
  data() {
    return {
      loading: false,
      avatarPath: '',
      userData: {},
      emailVerificationSent: false,
      phoneVerificationSent: false,
    }
  },
  computed: {
    emailVerified() {
      return this.getValueFromSource(this.userData, 'meta.email_verified', false)
    },
    phoneVerified() {
      return this.getValueFromSource(this.userData, 'meta.phone_verified', false)
    },
    fullName() {
      return this.getValueFromSource(this.currentUser, 'member_profile.full_name', '');
    },
  },
  watch: {
    currentUser: {
      handler() {
        this.userData = cloneDeep(this.currentUser)
        this.avatarPath = this.getValueFromSource(this.currentUser, 'member_profile.avatar.path', '');
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    async requestPhoneVerification() {
      try {
        this.loading = true;
        await this.useJwt().mainService.auth.requestPhoneVerification();
        this.$router.push({ name: 'auth-verify-phone' });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async resendVerificationEmail() {
      try {
        this.loading = true;
        await this.useJwt().mainService.auth.resendAccountConfirmationEmail({ username: this.userData.email });
        this.emailVerificationSent = true;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: "Email verification sent successfully. Check your email for verification link"
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
input[readonly] {
  cursor: default;
  background-color: transparent;
}
</style>
